<script setup lang="ts">
import { mdiCheckDecagram, mdiLock } from '@mdi/js'
import LargeDialog from './LargeDialog.vue'
import type { SkillCard } from '~/composables/useApi'

/**
 * スキルのカタログ（#933）。手に入れたスキルを眺める。
 *
 * **まだ手に入れていないスキルは「？」の枠だけ**。名前も中身もサーバが送ってこない
 * （`services/skill_catalog.py`。冒険の手記 #900 と同じ方針）。手に入れたスキルでも、
 * まだ届いていない型は数だけ来るので「？」を並べる。
 * リード文は置かない（手記と同じ。枠が並んでいれば説明は要らない）。
 *
 * **型のあるスキル（Pyxel の基本・Python の書き方・いくつものワンダーで深まるテクニック）は、型を 1 枚ずつ並べる**
 * （2026-10-04 doi）。集める単位は型のほう。型を選ぶと、その型の解放条件と中身を右に出す。
 *
 * **文法（Pyxel の基本の型・Python の書き方）は、まだでも「？」ではなく名前を薄く出す**（#956。次の目標として
 * 見せる）。ワンダーのクリアで手に入る技法・ジャンルは「？」のまま（ネタばらしになる）。
 * Python の書き方は「使った」（クリアしたワンダーの穴で打った）ものが手に入れた扱い。ほかのスキルと同じカードで来る
 * （#960）。まだの書き方のうち、段の経験値に届いていないもの（visible が false）はさらに薄く出す。
 */
const dialog = defineModel<boolean>()

const api = useApi()
const skills = ref<SkillCard[]>([])
/** 選んでいるもの。typeIndex があれば型、無ければスキル */
interface Selection { skillIndex: number, typeIndex: number | null }
const selection = ref<Selection | null>(null)
const failed = ref(false)

// 開くたびに取り直す（お知らせのあとに開いたときも最新が出るように）
watch(dialog, async (open) => {
  if (!open) return
  failed.value = false
  try {
    const res = await api.listSkills()
    skills.value = res.skills
    const first = groups.value.flatMap(g => g.items).find(item => item.acquired)
    selection.value = first ? select(first) : null
  }
  catch (e) {
    console.error('[SkillListDialog] failed to load skills', e)
    failed.value = true
  }
}, { immediate: true })

interface ListItem extends Selection {
  key: string
  label: string | null
  acquired: boolean
  /** まだだが段の経験値に届いている（Python の書き方。写経の対象） */
  visible?: boolean
  /** 並び（テクニック・ジャンルはワンダーの順。#957） */
  order?: number | null
}

const select = (item: ListItem): Selection =>
  ({ skillIndex: item.skillIndex, typeIndex: item.typeIndex })

const skillItems = (kind: SkillCard['kind']) =>
  skills.value.flatMap((skill, skillIndex): ListItem[] => {
    if (skill.kind !== kind) return []
    // 型のあるスキル（Pyxel の基本・いくつものワンダーで深まるテクニック）は型を 1 枚ずつ（スキルそのものがまだなら「？」1 枚）
    if (skill.types.length) {
      return skill.types.map((t, typeIndex) => ({
        key: `${skill.skillId}-${typeIndex}`,
        label: t.label ?? null,
        acquired: t.acquired,
        visible: t.visible ?? undefined,
        order: t.order ?? null,
        skillIndex,
        typeIndex,
      }))
    }
    return [{
      key: skill.skillId ?? `locked-${skillIndex}`,
      label: skill.name ?? null,
      acquired: skill.acquired,
      order: skill.order ?? null,
      skillIndex,
      typeIndex: null,
    }]
  })

/**
 * 実績画面（Steam など）の定石にそろえる（2026-10-05 doi: スキル画面は一種の実績システム）:
 * - カテゴリーごとに「手に入れた数 / 全部の数」と進み具合のバー
 * - 手に入れたものが先、まだのものは後（文法は、見えるものが先・まだ見えないものが後。それぞれの中は元の順）
 * - 名前を出さないもの（技法・ジャンルのまだ）は 1 枚ずつ「？」を並べず、「？ あと N」の 1 枚にまとめる（隠し実績）
 */
const rank = (item: ListItem) => item.acquired ? 0 : item.visible === false ? 2 : 1
const makeGroup = (title: string, all: ListItem[]) => {
  const named = all.filter(item => item.acquired || item.label)
  return {
    title,
    acquired: all.filter(item => item.acquired).length,
    total: all.length,
    items: named.map((item, i) => ({ item, i }))
      .sort((a, b) => rank(a.item) - rank(b.item)
        || (a.item.order ?? Infinity) - (b.item.order ?? Infinity)
        || a.i - b.i)
      .map(({ item }) => item),
    hidden: all.length - named.length,
  }
}
const groups = computed(() => [
  makeGroup('Pyxel の基本', skillItems('pyxel')),
  makeGroup('Python の書き方', skillItems('grammar')),
  makeGroup('ゲーム開発テクニック', skillItems('technique')),
  makeGroup('ジャンル', skillItems('genre')),
].filter(g => g.total))

const isSelected = (item: ListItem) =>
  selection.value?.skillIndex === item.skillIndex
  && selection.value?.typeIndex === item.typeIndex

/** 一覧の枠の中身: 手に入れたもの・文法（まだでも名前）は名前、技法・ジャンルのまだは「？」 */
const itemText = (item: ListItem) => item.acquired || item.label ? item.label : '？'

/** カードの状態（見た目は SkillChip が持つ。#966 でお知らせと共通にした） */
const chipState = (item: ListItem) => {
  if (item.acquired) return 'acquired' as const
  if (!item.label) return 'locked' as const
  return item.visible === false ? 'far' as const : 'named' as const
}

const testId = (item: ListItem) => {
  const what = item.typeIndex === null ? 'skill' : 'skill-type'
  return `${what}-${item.acquired ? 'acquired' : 'locked'}`
}

/** 出典は「題名 — URL」の形（seed/skill_master の history.sources）。URL があれば題名をリンクにする */
const parseSource = (source: string) => {
  const m = source.match(/^(.*?)\s+—\s+(https?:\/\/\S+)$/)
  return m ? { title: m[1], url: m[2] } : { title: source, url: null }
}

const selected = computed(() =>
  selection.value === null ? null : skills.value[selection.value.skillIndex] ?? null,
)
/** 例（お手本の関数）も、型を選んでいれば型のもの（#957） */
const shownExamples = computed(() => selectedType.value
  ? (selectedType.value.example ? [selectedType.value.example] : [])
  : selected.value?.examples ?? [])
/** コツも、型を選んでいれば型のもの（#960） */
const shownTips = computed(() =>
  selectedType.value ? selectedType.value.tips ?? [] : selected.value?.tips ?? [])
/** 改造ポイントも、型を選んでいれば型のもの（#957） */
const shownRemixPoints = computed(() =>
  selectedType.value ? selectedType.value.remixPoints ?? [] : selected.value?.remixPoints ?? [])
/** 作れるものも、型を選んでいれば型のもの（親のスキルの文をくり返さない。#957） */
const shownApplications = computed(() =>
  selectedType.value ? selectedType.value.applications ?? [] : selected.value?.applications ?? [])
/** まだのものは、説明の冒頭と「習得すると読めます」を出す（何もないとバグに見える。2026-10-10 doi） */
const locked = computed(() => {
  const target = selectedType.value ?? selected.value
  return target && !target.acquired ? { teaser: target.teaser ?? null } : null
})
/** 歴史は、型を選んでいれば型の歴史（型ごとに違う。2026-10-04 doi）、スキルならスキルの歴史 */
const shownHistory = computed(() => selectedType.value ? selectedType.value.history : selected.value?.history ?? [])
const kindTitle: Record<SkillCard['kind'], string> = { pyxel: 'Pyxel の基本', grammar: 'Python の書き方', technique: 'ゲーム開発テクニック', genre: 'ジャンル' }
const selectedType = computed(() =>
  selection.value?.typeIndex == null ? null : selected.value?.types[selection.value.typeIndex] ?? null,
)

/**
 * 習得可能場所（2026-10-10 doi）: どのスキルも「解放」して写経で「習得」する。習得できる場所を読点で並べる
 * （例:「ゆりかご（経験値 10 以上）、はじまりの小屋、赤のクリスタル」）。
 * - **ゆりかご**: 羽根筆の見本を写経（偶然の出会い・無料）。経験値で解放される
 * - **ワンダー**: 穴を写経（一気に・ショートカット・有料）。アンロックで行ける
 * いま行ける場所（解放済み・アンロック済み）は強調する（解放されているがまだ習得していない、が分かるように）。
 * 習得はどれも写経なので、習得したものには「習得済」だけ。テクニック・ジャンルはワンダーだけ
 */
type Place = { name: string, open: boolean, area: string }
/** 行ける場所のチップの色（淡い地・枠）。エリアごと（2026-10-10 doi。仮の色。習得済のティールとは別） */
const AREA_COLORS: Record<string, { bg: string, border: string }> = {
  cradle: { bg: '#f6e7c3', border: '#b8862f' }, // 羽根筆のゆりかご（羽根筆の金茶）
  wld_beginnersPort: { bg: '#dbe8f6', border: '#2f6fae' }, // はじまりの港（海の青）
  wld_slumberingBeach: { bg: '#fbe5c8', border: '#d08a2c' }, // まどろみの浜辺（砂のオレンジ）
  wld_silverLeafForest: { bg: '#d9eedb', border: '#3f8a4a' }, // シルバーリーフの森（緑）
  wld_crystalWaveIsland: { bg: '#e8def6', border: '#7a4fb5' }, // クリスタルウェーブ島（紫）
}
const DEFAULT_COLORS = { bg: '#efe6d6', border: '#8a6d45' }
const placeStyle = (place: Place) => {
  if (!place.open) return undefined
  const c = AREA_COLORS[place.area] ?? DEFAULT_COLORS
  return { backgroundColor: c.bg, borderColor: c.border }
}
const places = (t: { acquired: boolean, learnedAt: string[], learnedAtAreas?: (string | null)[], unlockedAt?: string[], exp?: number | null, copyable?: boolean, visible?: boolean | null }): Place[] => {
  const list: Place[] = []
  if (t.copyable) list.push({ name: t.exp ? `ゆりかご（経験値 ${t.exp} 以上）` : 'ゆりかご', open: t.visible !== false, area: 'cradle' })
  t.learnedAt.forEach((name, i) => list.push({ name, open: !!t.unlockedAt?.includes(name), area: t.learnedAtAreas?.[i] ?? '' }))
  if (!list.length && t.exp != null) list.push({ name: t.exp === 0 ? '最初から' : `経験値 ${t.exp} 以上`, open: false, area: '' })
  // 届く場所が見えていない・まだ決まっていないは区別せず「？」（2026-10-05 doi）
  return list.length ? list : [{ name: '？', open: false, area: '' }]
}
</script>

<template>
  <LargeDialog
    v-model="dialog"
    title="スキル"
    :cancel-button="{ title: 'とじる' }"
    :content-unit-scale="0.85"
  >
    <!-- スキルが何に使えるか（2026-10-06 doi） -->
    <template #hint>
      <span class="skill-catalog__lead">
        エンコーディアの探索で身につけたスキルは、羽根筆のゆりかごで羽根筆が魔導書を書くときに使われます。
      </span>
    </template>
    <template #default>
      <p
        v-if="failed"
        class="skill-catalog__failed"
      >
        いまは開けないみたいだ。
      </p>
      <div
        v-else
        class="skill-catalog"
        data-testid="skill-catalog"
      >
        <nav class="skill-catalog__list">
          <section
            v-for="group in groups"
            :key="group.title"
            class="skill-catalog__group"
          >
            <h3 class="skill-catalog__group-title">
              <span>{{ group.title }}</span>
              <span
                class="skill-catalog__count"
                data-testid="skill-group-count"
              >{{ group.acquired }} 個習得済</span>
            </h3>
            <div class="skill-catalog__items">
              <SkillChip
                v-for="item in group.items"
                :key="item.key"
                as="button"
                :label="itemText(item)"
                :state="chipState(item)"
                :selected="isSelected(item)"
                :data-testid="testId(item)"
                @click="selection = select(item)"
              />
              <SkillChip
                v-if="group.hidden"
                state="hidden"
                :label="`？ あと ${group.hidden}`"
                data-testid="skill-locked"
              />
            </div>
          </section>
        </nav>

        <article
          v-if="selected"
          class="skill-catalog__detail"
          data-testid="skill-detail"
        >
          <!-- 型を選んでいるとき: 型の名前と解放条件。その下に親のスキル -->
          <template v-if="selectedType">
            <p class="skill-catalog__parent">
              {{ selected.name }}
            </p>
            <h2 class="skill-catalog__name">
              {{ selectedType.label }}
              <v-chip
                v-if="selectedType.acquired"
                size="small"
                variant="flat"
                class="skill-catalog__done"
                data-testid="skill-done"
              >
                <v-icon
                  :icon="mdiCheckDecagram"
                  start
                  color="#2e8b57"
                />
                習得済
              </v-chip>
            </h2>
            <div
              class="skill-catalog__learned"
              data-testid="skill-steps"
            >
              <span class="skill-catalog__places-title">習得可能場所</span>
              <span class="skill-catalog__places">
                <v-chip
                  v-for="place in places(selectedType)"
                  :key="place.name"
                  size="small"
                  :variant="'outlined'"
                  color="#6b5a44"
                  :style="placeStyle(place)"
                  class="skill-catalog__place"
                  :class="{ 'skill-catalog__place--open': place.open }"
                  data-testid="skill-place"
                >
                  {{ place.name }}
                </v-chip>
              </span>
            </div>
            <p
              v-if="selectedType.description"
              class="skill-catalog__essence"
            >
              {{ selectedType.description }}
            </p>
            <p
              v-if="selectedType.appeal"
              class="skill-catalog__appeal"
            >
              {{ selectedType.appeal }}
            </p>
          </template>
          <template v-else>
            <p class="skill-catalog__parent">
              {{ kindTitle[selected.kind] }}
            </p>
            <h2 class="skill-catalog__name">
              {{ selected.name }}
              <v-chip
                v-if="selected.acquired"
                size="small"
                variant="flat"
                class="skill-catalog__done"
                data-testid="skill-done"
              >
                <v-icon
                  :icon="mdiCheckDecagram"
                  start
                  color="#2e8b57"
                />
                習得済
              </v-chip>
            </h2>
            <div
              class="skill-catalog__learned"
              data-testid="skill-steps"
            >
              <span class="skill-catalog__places-title">習得可能場所</span>
              <span class="skill-catalog__places">
                <v-chip
                  v-for="place in places(selected)"
                  :key="place.name"
                  size="small"
                  :variant="'outlined'"
                  color="#6b5a44"
                  :style="placeStyle(place)"
                  class="skill-catalog__place"
                  :class="{ 'skill-catalog__place--open': place.open }"
                  data-testid="skill-place"
                >
                  {{ place.name }}
                </v-chip>
              </span>
            </div>
            <p
              v-if="selected.description"
              class="skill-catalog__essence"
            >
              {{ selected.description }}
            </p>
            <!-- ゲームにもたらす面白さ（#957。本質は AI に渡す芯なので、面白さは別に持つ） -->
            <p
              v-if="selected.appeal"
              class="skill-catalog__appeal"
              data-testid="skill-appeal"
            >
              {{ selected.appeal }}
            </p>
          </template>

          <template v-if="locked">
            <p
              v-if="locked.teaser"
              class="skill-catalog__essence skill-catalog__teaser"
              data-testid="skill-teaser"
            >
              {{ locked.teaser }}
            </p>
            <p
              class="skill-catalog__locked"
              data-testid="skill-locked-note"
            >
              <v-icon
                :icon="mdiLock"
                size="small"
              />
              説明・コードの例・歴史は、習得すると読めます
            </p>
          </template>

          <!-- 型を選んでいるときは親のスキルの本質・作れるものを出さない（どの型でも同じ文になる。2026-10-05 doi）。
               型ごとの「こんな応用も」を出す -->
          <!-- 中身の並びは全種類で同じ: 説明 → 例 → こんな応用も → 改造ポイント・コツ → 歴史（#957） -->
          <template v-if="shownExamples.length">
            <h4 class="skill-catalog__heading">
              例
            </h4>
            <div
              v-for="(ex, i) in shownExamples"
              :key="i"
              class="skill-catalog__example"
            >
              <p
                v-if="ex.where"
                class="skill-catalog__example-where"
              >
                『{{ ex.where }}』の魔導書より
              </p>
              <pre
                class="skill-catalog__code"
                data-testid="skill-example"
              ><span
                v-for="(line, n) in ex.code.split('\n')"
                :key="n"
                class="skill-catalog__code-line"
                :class="{ 'skill-catalog__code-line--focus': ex.highlight?.includes(n) }"
              >{{ line }}</span></pre>
            </div>
          </template>

          <template v-if="shownApplications.length">
            <h4 class="skill-catalog__heading">
              こんな応用も
            </h4>
            <ul class="skill-catalog__bullets">
              <li
                v-for="app in shownApplications"
                :key="app"
              >
                {{ app }}
              </li>
            </ul>
          </template>

          <template v-if="!selectedType && (selected.known.length || selected.notYetCount)">
            <h4 class="skill-catalog__heading">
              身につけた型
            </h4>
            <div class="skill-catalog__chips">
              <span
                v-for="label in selected.known"
                :key="label"
                class="skill-catalog__chip"
              >{{ label }}</span>
              <span
                v-for="n in selected.notYetCount"
                :key="`not-yet-${n}`"
                class="skill-catalog__chip skill-catalog__chip--unknown"
                data-testid="skill-not-yet"
              >？</span>
            </div>
          </template>

          <!-- 改造ポイント（人向けの言い方。AI に渡す「変えてよい軸」とは別に持つ。#957） -->
          <template v-if="shownRemixPoints.length">
            <h4 class="skill-catalog__heading">
              改造ポイント
            </h4>
            <ul class="skill-catalog__bullets">
              <li
                v-for="point in shownRemixPoints"
                :key="point"
              >
                {{ point }}
              </li>
            </ul>
          </template>

          <template v-if="shownTips.length">
            <h4 class="skill-catalog__heading">
              コツ
            </h4>
            <ul class="skill-catalog__bullets">
              <li
                v-for="note in shownTips"
                :key="note"
              >
                {{ note }}
              </li>
            </ul>
          </template>

          <!-- 歴史（その技法が使われた作品と時代）。文章は出典つきで後から入る（#933） -->
          <template v-if="shownHistory.length">
            <h4 class="skill-catalog__heading">
              歴史
            </h4>
            <div
              v-for="(entry, i) in shownHistory"
              :key="i"
              class="skill-catalog__history"
            >
              <p class="skill-catalog__text">
                {{ entry.text }}
              </p>
              <p class="skill-catalog__sources">
                出典:
                <template
                  v-for="(source, j) in entry.sources"
                  :key="source"
                >
                  <span v-if="j"> / </span>
                  <a
                    v-if="parseSource(source).url"
                    :href="parseSource(source).url!"
                    target="_blank"
                    rel="noopener noreferrer"
                  >{{ parseSource(source).title }}</a>
                  <span v-else>{{ parseSource(source).title }}</span>
                </template>
              </p>
            </div>
          </template>
        </article>
      </div>
    </template>
  </LargeDialog>
</template>

<style scoped>
/* 共有コンポーネントなので dvw ではなく calc(N * var(--u))（responsive_sizing.md）。 */
/* 左の一覧と右の中身は**別々にスクロールする**（2026-10-04 doi）。器の本文（高さ固定・overflow-y: auto）
   いっぱいに広げ、器そのものはスクロールさせずに列ごとに overflow-y: auto を持たせる。 */
.skill-catalog {
  display: grid;
  grid-template-columns: minmax(0, 2fr) minmax(0, 3fr);
  /* 行を器の高さに固定する（auto だと中身に合わせて伸び、器のほうがスクロールしてしまう） */
  grid-template-rows: minmax(0, 1fr);
  gap: max(12px, calc(1.5 * var(--u)));
  height: 100%;
  color: #3a2f1c;
}

.skill-catalog__list,
.skill-catalog__detail {
  min-height: 0;
  max-height: 100%;
  overflow-y: auto;
  overscroll-behavior: contain;
}

.skill-catalog__list {
  display: flex;
  flex-direction: column;
  gap: max(10px, calc(1.2 * var(--u)));
  /* スクロールバーが枠に重ならないように */
  padding-right: max(4px, calc(0.5 * var(--u)));
}

.skill-catalog__group-title {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  font-size: max(12px, calc(1.4 * var(--u)));
  font-weight: bold;
  /* 総数のバーを外したので、見出しの下に区切り線（総数は増え続けて分母にならない。2026-10-10 doi） */
  margin-bottom: max(6px, calc(0.7 * var(--u)));
  padding-bottom: max(2px, calc(0.3 * var(--u)));
  border-bottom: 1px solid rgba(92, 62, 30, 0.25);
  opacity: 0.8;
}

.skill-catalog__items {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: max(6px, calc(0.7 * var(--u)));
}

/* 面白さ: 説明の 2 段目。左に線を引いて、何をするか（1 段目）と読み分けられるように */
.skill-catalog__appeal {
  font-size: max(11px, calc(1.45 * var(--u)));
  line-height: 1.7;
  margin-top: max(6px, calc(0.8 * var(--u)));
  padding-left: max(8px, calc(1 * var(--u)));
  border-left: 3px solid #c9a440;
  color: #5b3a1a;
}

/* 題の下の一文。器の補助枠（hint）の既定は本文と同じ大きさの太字なので、ここだけ小さく細く（1 行に収める） */
.skill-catalog__lead {
  font-size: max(11px, calc(1.4 * var(--u)));
  font-weight: normal;
}

.skill-catalog__code {
  font-family: "Source Code Pro", ui-monospace, monospace;
  font-size: max(11px, calc(1.35 * var(--u)));
  line-height: 1.5;
  padding: max(6px, calc(0.8 * var(--u))) max(8px, calc(1 * var(--u)));
  background: rgba(92, 62, 30, 0.1);
  border: 1px solid rgba(92, 62, 30, 0.25);
  color: #2a1f10;
  font-weight: normal;
  opacity: 1;
  border-radius: max(4px, calc(0.6 * var(--u)));
  overflow-x: auto;
  white-space: pre;
}

/* 例はスクロールさせない（2026-10-06 doi）。長いお手本はサーバが「中略」で縮めてくる。はみ出す行は折り返す */
.skill-catalog__detail .skill-catalog__code {
  overflow: visible;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
}

.skill-catalog__example + .skill-catalog__example {
  margin-top: max(8px, calc(1 * var(--u)));
}

.skill-catalog__example-where {
  font-size: max(10px, calc(1.2 * var(--u)));
  opacity: 0.75;
  margin-bottom: 2px;
}

/* 例の中で、そのスキルの芯になる行を光らせる（#957） */
.skill-catalog__code-line {
  display: block;
  /* 空の行も 1 行ぶんの高さを持たせる */
  min-height: 1.5em;
}

.skill-catalog__code-line--focus {
  background: rgba(201, 164, 64, 0.35);
  box-shadow: -4px 0 0 #c9a440;
}

.skill-catalog__count {
  font-weight: normal;
  font-variant-numeric: tabular-nums;
}

/* 右の中身。見出しと本文の差で読ませ、字は小さめ（2026-10-05 doi: 一度に見える量を多く） */
.skill-catalog__detail {
  padding: max(14px, calc(2 * var(--u))) max(16px, calc(2.4 * var(--u)));
  background: rgba(255, 250, 238, 0.9);
  border: 2px solid rgba(92, 62, 30, 0.35);
  border-radius: max(6px, calc(0.8 * var(--u)));
  align-self: start;
  font-weight: normal;
}

/* 分類（Pyxel アプリの基本の型 / Python の書き方 …）。名前の上に小さく */
.skill-catalog__parent {
  font-size: max(11px, calc(1.25 * var(--u)));
  font-weight: bold;
  color: #8a5a1c;
  letter-spacing: 0.05em;
}

.skill-catalog__name {
  /* 習得済のチップとタイトルの縦の中心をそろえる（2026-10-10 doi） */
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  font-size: max(16px, calc(2.2 * var(--u)));
  line-height: 1.3;
  margin: max(2px, calc(0.2 * var(--u))) 0 max(8px, calc(1 * var(--u)));
}

/* 習得可能場所 / 習得済: 題名のすぐ下の 1 行（2026-10-10 doi） */
.skill-catalog__learned {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr);
  align-items: start;
  column-gap: max(6px, calc(0.8 * var(--u)));
  font-size: max(11px, calc(1.4 * var(--u)));
  margin-bottom: max(10px, calc(1.4 * var(--u)));
}

/* 見出しはチップにしない（場所のチップと見分ける。2026-10-10 doi） */
.skill-catalog__places-title {
  /* チップの 1 行目と縦の中心をそろえる（v-chip の small は高さ 24px） */
  display: flex;
  align-items: center;
  min-height: 24px;
  font-size: max(10px, calc(1.2 * var(--u)));
  font-weight: bold;
  color: #5c3e1e;
}

/* 習得可能場所は Vuetify のチップで並べる（2026-10-10 doi）。行ける場所（解放済み・アンロック済み）は塗り、まだの場所は枠だけ */
/* 2 列の表: 左の列に見出し（習得可能場所）、右の列にチップ。チップは右の列の中だけで折り返し、見出しの下には
   回り込まない（2026-10-10 doi） */
.skill-catalog__places {
  display: flex;
  flex-wrap: wrap;
  gap: max(4px, calc(0.5 * var(--u)));
}

.skill-catalog__place {
  font-size: max(10px, calc(1.2 * var(--u)));
}

/* 行ける場所: 淡い地＋エリア色の枠＋黒い文字（色は placeStyle。習得済とは別のデザイン。2026-10-10 doi） */
.skill-catalog__place.skill-catalog__place--open {
  font-weight: bold;
  color: #000 !important;
  border-width: 1px;
  border-style: solid;
}

/* 習得済: タイトルの後ろのチップ。ギザギザの星にチェック（check-decagram）＋文字。黒い文字に淡い緑の地・
   緑の枠（2026-10-10 doi） */
.skill-catalog__done {
  margin-left: max(8px, calc(1 * var(--u)));
  /* タイトルより一回り小さく（枠のぶん大きく見えるので）・緑寄り（2026-10-10 doi） */
  font-size: max(13px, calc(1.7 * var(--u))) !important;
  height: auto !important;
  padding: max(1px, calc(0.15 * var(--u))) max(8px, calc(1 * var(--u))) !important;
  font-weight: bold;
  color: #000 !important;
  background: #d3eed6 !important;
  border: 1px solid #2e8b57;
}

.skill-catalog__essence {
  font-size: max(11px, calc(1.45 * var(--u)));
  line-height: 1.7;
}

.skill-catalog__text,
.skill-catalog__bullets {
  font-size: max(11px, calc(1.45 * var(--u)));
  line-height: 1.75;
}

/* 見出しは色と下線で区切る（薄くしただけだと本文と見分けにくい） */
.skill-catalog__heading {
  font-size: max(11px, calc(1.35 * var(--u)));
  font-weight: bold;
  color: #8a5a1c;
  margin: max(16px, calc(2.2 * var(--u))) 0 max(6px, calc(0.8 * var(--u)));
  padding-bottom: max(2px, calc(0.3 * var(--u)));
  border-bottom: 1px solid rgba(138, 90, 28, 0.3);
}

.skill-catalog__bullets {
  padding-left: 1.2em;
}

.skill-catalog__chips {
  display: flex;
  flex-wrap: wrap;
  gap: max(4px, calc(0.6 * var(--u)));
}

.skill-catalog__chip {
  font-size: max(11px, calc(1.45 * var(--u)));
  padding: 2px max(8px, calc(1 * var(--u)));
  border-radius: 999px;
  background: #f1e2bf;
  border: 1px solid rgba(92, 62, 30, 0.35);
}

.skill-catalog__chip--unknown {
  background: transparent;
  border-style: dashed;
}

/* まだのもの: 説明の冒頭は薄く、その下に鍵つきの一言（2026-10-10 doi） */
.skill-catalog__teaser {
  opacity: 0.6;
}

.skill-catalog__locked {
  display: flex;
  align-items: center;
  gap: max(4px, calc(0.5 * var(--u)));
  font-size: max(11px, calc(1.3 * var(--u)));
  color: #6b5a44;
  margin-top: max(6px, calc(0.8 * var(--u)));
}

/* 歴史は 1 話ずつ左に線を引いて、出典はその下に小さく */
.skill-catalog__history {
  padding-left: max(10px, calc(1.2 * var(--u)));
  border-left: 3px solid rgba(138, 90, 28, 0.35);
}

.skill-catalog__history + .skill-catalog__history {
  margin-top: max(10px, calc(1.4 * var(--u)));
}

.skill-catalog__sources {
  font-size: max(11px, calc(1.3 * var(--u)));
  line-height: 1.5;
  margin-top: max(4px, calc(0.5 * var(--u)));
  opacity: 0.75;
}

.skill-catalog__sources a {
  color: inherit;
}

.skill-catalog__failed {
  font-size: max(12px, calc(1.4 * var(--u)));
}

/* スマホは 1 列に積むので、列ごとのスクロールはやめて器ごとスクロールさせる */
@media (max-width: 768px) {
  .skill-catalog {
    grid-template-columns: 1fr;
    grid-template-rows: auto;
    height: auto;
  }

  .skill-catalog__list,
  .skill-catalog__detail {
    max-height: none;
    overflow-y: visible;
  }
}
</style>
